import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import { apiService } from '../../../services/apiService';
import { usePerimetreReferentiel } from '../../../composants/PerimetreSelector/usePerimetreReferentiel';
import CarteAffectation from '../../../composants/AffectationCarte/CarteAffectation';
import { libelleDept } from '../../../composants/AffectationCarte/affectationColors';
import ViewSwitch from '../../../composants/ViewSwitch/ViewSwitch';
import Banner from '../../../composants/Banner/Banner';
import './PrestataireZonesCarte.css';

// "Affectes" fictifs (une couleur par etat). Reutilise la carte d'AFFECTATION de la gestion
// d'equipe pour une parite visuelle et de taille exacte. Palette fermee (oranges du design system).
const COUVERT = 1;      // zone d'intervention libre choisie
const TITULAIRE = 2;    // deja titulaire de l'AC sur ce lot (non cliquable en libre)
const COULEUR_COUVERT = '#f97316';   // orange primaire
const COULEUR_TITULAIRE = '#c2410c'; // orange brule (distinct, dans la palette)

const MSG_DEJA_TITULAIRE = 'Vous êtes déjà titulaire de l’accord-cadre sur cette zone pour ce lot : elle est couverte automatiquement, inutile de l’ajouter en intervention libre.';

interface LotDep {
  lot_technique_id: number;
  lot_technique_nom?: string;
  lot_technique_code?: string;
  departement_id: number;
  is_locked?: boolean;
}

interface PrestataireZonesCarteProps {
  // 'libre' : EDITABLE (le prestataire choisit ses zones). 'ac' : LECTURE SEULE (titulaire).
  mode: 'ac' | 'libre';
}

type Etat = 'couvert' | 'titulaire' | 'libre';

// Lot regional (on choisit des REGIONS entieres) : codes 1xx ou 3xx. Sinon departemental (2xx).
// Meme regle que le reste de l'app (cf. SectionChoixLots / affectation).
const isLotRegional = (code?: string | number): boolean => {
  const n = Number(code);
  return (n >= 100 && n < 200) || (n >= 300 && n < 400);
};

/**
 * Perimetre geographique d'intervention du prestataire, par lot technique. Bascule Carte / Liste
 * (meme composants et meme taille que la gestion d'equipe). On couvre des REGIONS entieres pour un
 * lot regional (1xx/3xx) ou des DEPARTEMENTS pour un lot departemental (2xx). Editable en mode libre
 * (auto-save), lecture seule en mode titulaire (AC). En libre, les zones deja titulaires de l'AC sont
 * marquees (orange brule) et non modifiables : un clic affiche un message.
 */
const PrestataireZonesCarte: React.FC<PrestataireZonesCarteProps> = ({ mode }) => {
  const { profile, updateLotsForLotTechnique, isSaving } = useProfile();
  const { referentiel } = usePerimetreReferentiel();
  const readOnly = mode === 'ac';
  const [view, setView] = useState<'carte' | 'liste'>('carte');
  // Filtre (menu deroulant, pas texte libre) : region (lot 1xx/3xx) ou departement (2xx) a cibler.
  const [filtre, setFiltre] = useState<number | null>(null);

  // Mode libre : catalogue complet de lots techniques (zones possibles sur n'importe quel lot).
  const [catalogue, setCatalogue] = useState<{ id: number; code: string; nom: string }[]>([]);
  useEffect(() => {
    if (mode !== 'libre') return;
    let cancelled = false;
    apiService.getLotsTechniques()
      .then((data: { id: number; code: string; nom: string }[]) => {
        if (!cancelled) setCatalogue(Array.isArray(data) ? data : []);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [mode]);

  const allLots = useMemo<LotDep[]>(
    () => ((profile?.lots?.lots_departements as LotDep[] | undefined) ?? []),
    [profile],
  );
  const modeLots = useMemo(
    () => allLots.filter((l) => (mode === 'ac' ? l.is_locked === true : l.is_locked !== true)),
    [allLots, mode],
  );

  const lotsTech = useMemo(() => {
    if (mode === 'libre') {
      return catalogue.map((lt) => ({ id: lt.id, nom: lt.nom, code: lt.code }));
    }
    const map = new Map<number, { id: number; nom: string; code: string }>();
    modeLots.forEach((l) => {
      if (!map.has(l.lot_technique_id)) {
        map.set(l.lot_technique_id, { id: l.lot_technique_id, nom: l.lot_technique_nom ?? '', code: l.lot_technique_code ?? '' });
      }
    });
    return Array.from(map.values());
  }, [mode, catalogue, modeLots]);

  const sortedLotsTech = useMemo(
    () => [...lotsTech].sort((a, b) => String(a.code).localeCompare(String(b.code))),
    [lotsTech],
  );
  const [selectedLot, setSelectedLot] = useState<number | null>(null);
  const effLot = selectedLot != null && sortedLotsTech.some((l) => l.id === selectedLot)
    ? selectedLot
    : (sortedLotsTech[0]?.id ?? null);
  const effLotObj = sortedLotsTech.find((l) => l.id === effLot) ?? null;
  const regional = isLotRegional(effLotObj?.code);

  // Le filtre depend du lot (region vs departement) : on le reinitialise a chaque changement de lot.
  useEffect(() => { setFiltre(null); }, [effLot]);

  // Departements couverts pour le lot courant (libres en mode libre, titulaires en AC).
  const coveredDeptIds = useMemo(
    () => new Set(modeLots.filter((l) => l.lot_technique_id === effLot).map((l) => l.departement_id)),
    [modeLots, effLot],
  );
  // Departements titulaires de l'AC pour ce lot : concept utile UNIQUEMENT en mode libre (zones
  // bloquees + message). En mode AC (lecture seule), l'ensemble est vide -> les zones couvertes
  // restent l'etat "couvert", sinon la liste read-only se filtrerait a vide.
  const titulaireDeptIds = useMemo(
    () => (mode === 'libre'
      ? new Set(allLots.filter((l) => l.lot_technique_id === effLot && l.is_locked === true).map((l) => l.departement_id))
      : new Set<number>()),
    [allLots, effLot, mode],
  );

  const deptsDeLaRegion = useMemo(() => {
    const m = new Map<number, number[]>();
    referentiel.forEach((r) => {
      const ids = r.departements.map((d) => d.id);
      ids.forEach((id) => m.set(id, ids));
    });
    return m;
  }, [referentiel]);

  // Carte : tous les departements du referentiel. Couvert -> orange, titulaire (libre) -> brule.
  const affecteParDept = useMemo(() => {
    const m = new Map<number, number | null>();
    referentiel.forEach((r) => r.departements.forEach((d) => {
      if (mode === 'libre' && titulaireDeptIds.has(d.id)) m.set(d.id, TITULAIRE);
      else m.set(d.id, coveredDeptIds.has(d.id) ? COUVERT : null);
    }));
    return m;
  }, [referentiel, coveredDeptIds, titulaireDeptIds, mode]);

  // Surlignage carte selon le filtre : departements de la region ciblee (lot regional) ou le
  // departement cible (lot departemental). Les autres s'attenuent. Null = pas de filtre.
  const surligneDepts = useMemo(() => {
    if (filtre == null) return null;
    if (regional) {
      const region = referentiel.find((r) => r.id === filtre);
      return region ? new Set(region.departements.map((d) => d.id)) : null;
    }
    return new Set([filtre]);
  }, [filtre, regional, referentiel]);

  const couleurParAffecte = useMemo(
    () => new Map<number, string>([[COUVERT, COULEUR_COUVERT], [TITULAIRE, COULEUR_TITULAIRE]]),
    [],
  );
  const nomParAffecte = useMemo(
    () => new Map<number, string>([[COUVERT, 'Zone d’intervention libre'], [TITULAIRE, 'Déjà titulaire (accord-cadre)']]),
    [],
  );
  const EMPTY = useMemo(() => new Set<number>(), []);

  // Message transitoire (clic sur une zone deja titulaire). Auto-efface au bout de quelques secondes.
  const [message, setMessage] = useState<string | null>(null);
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);
  const flashMessage = (m: string) => {
    setMessage(m);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setMessage(null), 5000);
  };

  // Applique un toggle sur un ensemble de departements (on n'ajoute/retire QUE les non-titulaires).
  const applyToggle = (rawTargets: number[]) => {
    if (readOnly || effLot == null) return;
    const targets = rawTargets.filter((id) => !titulaireDeptIds.has(id));
    if (targets.length === 0) { flashMessage(MSG_DEJA_TITULAIRE); return; }
    const next = new Set(coveredDeptIds);
    const allCovered = targets.every((id) => next.has(id));
    targets.forEach((id) => (allCovered ? next.delete(id) : next.add(id)));
    updateLotsForLotTechnique(effLot, Array.from(next));
  };

  // Clic carte (un departement) : sa region entiere (1xx/3xx) ou le departement (2xx).
  const handleToggleDept = (deptId: number) => {
    if (titulaireDeptIds.has(deptId)) { flashMessage(MSG_DEJA_TITULAIRE); return; }
    applyToggle(regional ? (deptsDeLaRegion.get(deptId) ?? [deptId]) : [deptId]);
  };

  // Etat d'un ensemble de departements (region ou departement seul).
  const etatDe = (deptIds: number[]): Etat => {
    const nonTit = deptIds.filter((id) => !titulaireDeptIds.has(id));
    if (deptIds.length > 0 && nonTit.length === 0) return 'titulaire';
    if (nonTit.length > 0 && nonTit.every((id) => coveredDeptIds.has(id))) return 'couvert';
    return 'libre';
  };

  // Lignes de la vue liste : regions (1xx/3xx) ou departements groupes par region (2xx).
  // En lecture seule on ne montre QUE les zones couvertes (le perimetre reel).
  const listeGroupes = useMemo(() => {
    return referentiel.map((r) => {
      const regionDeptIds = r.departements.map((d) => d.id);
      const depts = r.departements
        .map((d) => ({ id: d.id, label: libelleDept(d.id), etat: etatDe([d.id]) }))
        .filter((d) => (readOnly ? d.etat === 'couvert' : true));
      return {
        id: r.id,
        // Code region en CHIFFRES (INSEE) = l'id (Guadeloupe 01, Ile-de-France 11, Auvergne-RA 84...),
        // pade a 2 chiffres ; pas le code alpha (GUA, IDF...).
        label: `${String(r.id).padStart(2, '0')} — ${r.nom}`,
        deptIds: regionDeptIds,
        etat: etatDe(regionDeptIds),
        depts,
      };
    }).filter((g) => (readOnly ? (regional ? g.etat === 'couvert' : g.depts.length > 0) : true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referentiel, coveredDeptIds, titulaireDeptIds, regional, readOnly]);

  // Lignes filtrees par le menu deroulant (region ou departement choisi).
  const filteredGroupes = useMemo(() => {
    if (filtre == null) return listeGroupes;
    if (regional) return listeGroupes.filter((g) => g.id === filtre);
    return listeGroupes
      .map((g) => ({ ...g, depts: g.depts.filter((d) => d.id === filtre) }))
      .filter((g) => g.depts.length > 0);
  }, [listeGroupes, filtre, regional]);

  if (sortedLotsTech.length === 0) {
    return (
      <p className="zones-carte-empty">
        {mode === 'ac' ? 'Vous n’êtes titulaire d’aucun lot.' : 'Aucun lot technique disponible.'}
      </p>
    );
  }

  const consigne = readOnly
    ? `${regional ? 'Régions' : 'Départements'} où vous êtes titulaire de l’accord-cadre pour ce lot (lecture seule).`
    : `Sélectionnez ${regional ? 'les régions' : 'les départements'} où vous souhaitez intervenir pour ce lot.`;

  const tag = (etat: Etat) =>
    etat === 'titulaire' ? <span className="zones-liste-tag is-titulaire">Titulaire</span>
      : etat === 'couvert' ? <span className="zones-liste-tag is-couvert">Couvert</span>
        : <span className="zones-liste-tag">—</span>;

  return (
    <div className="zones-carte">
      <div className="zones-carte-toolbar">
        <label className="zones-carte-field">
          <span>Lot technique</span>
          <select value={effLot ?? ''} onChange={(e) => setSelectedLot(Number(e.target.value))}>
            {sortedLotsTech.map((lt) => (
              <option key={lt.id} value={lt.id}>{lt.code} — {lt.nom}</option>
            ))}
          </select>
        </label>
        <label className="zones-carte-field zones-carte-field--filtre">
          <span>{regional ? 'Filtrer une région' : 'Filtrer un département'}</span>
          <select value={filtre ?? ''} onChange={(e) => setFiltre(e.target.value ? Number(e.target.value) : null)}>
            <option value="">{regional ? 'Toutes les régions' : 'Tous les départements'}</option>
            {regional
              ? listeGroupes.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)
              : listeGroupes.map((g) => (
                  <optgroup key={g.id} label={g.label}>
                    {g.depts.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </optgroup>
                ))}
          </select>
        </label>
        <ViewSwitch
          value={view}
          options={[{ value: 'carte', label: 'Carte' }, { value: 'liste', label: 'Liste' }]}
          onChange={setView}
          ariaLabel="Basculer carte / liste"
        />
      </div>

      <Banner variant="info" className="zones-carte-consigne" message={consigne} />
      {message && <Banner variant="warning" className="zones-carte-consigne" message={message} />}

      {view === 'carte' ? (
        <div className="zones-carte-mapcard">
          <CarteAffectation
            affecteParDept={affecteParDept}
            couleurParAffecte={couleurParAffecte}
            nomParAffecte={nomParAffecte}
            selection={EMPTY}
            onToggle={handleToggleDept}
            surligneDepts={surligneDepts}
            saving={mode === 'libre' && isSaving}
            readOnly={readOnly}
          />
        </div>
      ) : (
        <div className={`zones-liste${readOnly ? ' is-readonly' : ''}`}>
          {filteredGroupes.length === 0 ? (
            <p className="zones-carte-empty">Aucune zone pour ce lot.</p>
          ) : regional ? (
            // Lot regional : une ligne par region.
            <table className="zones-liste-table">
              <tbody>
                {filteredGroupes.map((g) => (
                  <tr
                    key={g.id}
                    className={`zones-liste-row is-${g.etat}${readOnly ? '' : ' is-actionnable'}`}
                    onClick={() => { if (!readOnly) applyToggle(g.deptIds); }}
                  >
                    <td className="zones-liste-col-sel">
                      <input type="checkbox" checked={g.etat !== 'libre'} readOnly tabIndex={-1} disabled={g.etat === 'titulaire'} />
                    </td>
                    <td className="zones-liste-nom">{g.label}</td>
                    <td className="zones-liste-col-tag">{tag(g.etat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            // Lot departemental : regions en tete de groupe, departements en lignes.
            <table className="zones-liste-table">
              <tbody>
                {filteredGroupes.map((g) => (
                  <React.Fragment key={g.id}>
                    <tr className="zones-liste-group">
                      <td colSpan={3}>{g.label}</td>
                    </tr>
                    {g.depts.map((d) => (
                      <tr
                        key={d.id}
                        className={`zones-liste-row is-${d.etat}${readOnly ? '' : ' is-actionnable'}`}
                        onClick={() => { if (readOnly) return; d.etat === 'titulaire' ? flashMessage(MSG_DEJA_TITULAIRE) : applyToggle([d.id]); }}
                      >
                        <td className="zones-liste-col-sel">
                          <input type="checkbox" checked={d.etat !== 'libre'} readOnly tabIndex={-1} disabled={d.etat === 'titulaire'} />
                        </td>
                        <td className="zones-liste-nom">{d.label}</td>
                        <td className="zones-liste-col-tag">{tag(d.etat)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {mode === 'libre' && titulaireDeptIds.size > 0 && (
        <div className="zones-carte-legende">
          <span className="zones-carte-legende-item">
            <span className="zones-carte-pastille" style={{ background: COULEUR_COUVERT }} />
            Zone d’intervention libre
          </span>
          <span className="zones-carte-legende-item">
            <span className="zones-carte-pastille" style={{ background: COULEUR_TITULAIRE }} />
            Déjà titulaire de l’accord-cadre (non modifiable)
          </span>
        </div>
      )}
    </div>
  );
};

export default PrestataireZonesCarte;
